/**
 * Dynamic loader for the Five9 CRM SDK script.
 *
 * The SDK is intentionally NOT included via a static `<script>` in
 * `public/index.html`. Instead we inject it here so it is only fetched
 * when the `five9Integration` master feature flag is enabled (this
 * module is only imported by `<Five9CrmAdapter>`, which `App.js`
 * conditionally mounts). This keeps the third-party script off the wire
 * for opted-out users and preserves the master gate's "no external
 * dependency risk when off" contract described in
 * `docs/five9-integration.md` § 11.
 *
 * The loader is idempotent: repeated calls return the same promise, so
 * multiple mounts (e.g. React StrictMode double-invoke, ownership
 * handover in single-tab lock) don't inject duplicate script tags.
 */

const SCRIPT_ID = 'five9-crm-sdk-loader';
/** Cached in-flight (or resolved) load promise. Reset to `null` after an
 *  error so a subsequent call can retry.
 *  @type {Promise<void>|null} */
let loadPromise = null;

/**
 * Injects the Five9 CRM SDK script exactly once and resolves when
 * `window.Five9.CrmSdk` becomes available (or the script `load` event
 * fires — the SDK may attach on load rather than synchronously).
 *
 * @param {string} url REACT_APP_FIVE9_SDK_URL value
 * @returns {Promise<void>} resolves on load, rejects on script error or
 *   missing URL
 */
export default function loadFive9Sdk(url) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadFive9Sdk: no window'));
  }
  if (window.Five9?.CrmSdk) return Promise.resolve();
  if (loadPromise) return loadPromise;
  if (!url) {
    return Promise.reject(new Error('loadFive9Sdk: REACT_APP_FIVE9_SDK_URL not set'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // A previous invocation already injected the tag.
      // - If it already succeeded (`data-loaded`) we resolve immediately.
      // - If it already failed (`data-failed`) we cannot rely on late
      //   `load`/`error` listeners (those events have already fired on the
      //   existing element and will never re-fire), so remove the stale tag
      //   and fall through to inject a fresh one for a real retry.
      // - Otherwise the load is still in-flight; attach listeners.
      if (existing.dataset.loaded === 'true' || window.Five9?.CrmSdk) {
        resolve();
        return;
      }
      if (existing.dataset.failed === 'true') {
        existing.remove();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (e) => reject(e));
        return;
      }
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = url;
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', () => {
      // Mark + remove the failed tag so the next call actually retries the
      // network request instead of attaching listeners to a dead node.
      script.dataset.failed = 'true';
      script.remove();
      loadPromise = null;
      reject(new Error(`loadFive9Sdk: failed to load ${url}`));
    });
    document.head.appendChild(script);
  });
  return loadPromise;
}

